import { Box, Typography, Stack, StackProps } from '@mui/material';
import WidgetWraper from 'components/Common/WidgetWraper';
import LoaderComponent from 'components/Loader';
import ReactECharts from 'echarts-for-react';
import { useEffect, useMemo, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import HighlightedText from 'components/GlobalSearch/HighlightText';
import { InfoTooltip, TooltipLabel } from '../InfoTooltip';

/* -------------------- Types -------------------- */

type SeriesVariant = 'area' | 'line';

type SeriesConfig = {
  key: string;
  name: string;
  color: string;
  variant?: SeriesVariant;
};

type AreaTrendChartProps = {
  title: string;
  subTitle?: string;
  data: Record<string, any>[] | null;

  xKey: string;
  xAxisLabel?: string;

  seriesConfig: SeriesConfig[];

  isCost?: boolean;

  hasTooltip?: boolean;
  tooltipLabel?: TooltipLabel;

  isLoading?: boolean;
  height?: string | number;
  minTitleHeight?: `${number}px`;

  /** Right-top controls (dropdown, filters, etc.) from parent */
  hasFilter?: boolean;
  children?: React.ReactNode;
  childrenContainerProps?: StackProps;

  containerProps?: StackProps;
};

/* -------------------- Helpers -------------------- */

const K_THRESHOLD = 1_000;

const normalizeChartData = (
  data: Record<string, any>[],
  xKey: string,
  seriesConfig: SeriesConfig[],
) => {
  const xAxis = data.map((item) => item?.[xKey]);

  const series = seriesConfig.map((cfg) => ({
    name: cfg.name,
    color: cfg.color,
    variant: cfg.variant ?? 'area',
    data: data.map((item) => Number(item?.[cfg.key]) || 0),
  }));

  return { xAxis, series };
};

const formatYAxisValue = (value: number, isCost: boolean) => {
  const abs = Math.abs(value);

  const withPrefix = (v: string) => (isCost ? `$${v}` : `${v}`);

  if (abs >= 1_000_000) return withPrefix(`${(abs / 1_000_000).toFixed(1)}M`);
  if (abs >= K_THRESHOLD) return withPrefix(`${(abs / 1_000).toFixed(1)}K`);
  return withPrefix(`${abs}`);
};

/* -------------------- Component -------------------- */

const AreaTrendChart = ({
  title,
  subTitle,
  data,
  xKey,
  xAxisLabel,
  seriesConfig,
  isCost = true,
  hasTooltip = false,
  tooltipLabel,
  isLoading = false,
  minTitleHeight = '38px',
  height = 320,
  hasFilter = true,
  children,
  childrenContainerProps,
  containerProps,
}: AreaTrendChartProps) => {
  const theme = useTheme();
  const colors = theme.palette;
  const chartRef = useRef<ReactECharts>(null);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      chartRef.current?.getEchartsInstance()?.resize();
    });

    const inst = chartRef.current?.getEchartsInstance?.();
    const dom = inst?.getDom?.();

    if (dom) resizeObserver.observe(dom);
    return () => resizeObserver.disconnect();
  }, []);

  const chartData = useMemo(() => {
    if (!data || data.length === 0) return null;
    return normalizeChartData(data, xKey, seriesConfig);
  }, [data, xKey, seriesConfig]);

  const option = useMemo(() => {
    const bg = colors?.widget?.background?.chart;
    const labelColor = colors?.widget?.chart?.labelColorSecondary;
    const legendColor = colors?.widget?.chart?.legendTextColor;

    if (!chartData) return { backgroundColor: bg };

    return {
      backgroundColor: bg,
      textStyle: { color: labelColor },

      tooltip: hasTooltip
        ? {
            trigger: 'axis',
            formatter: (params: any[]) => {
              const xVal = params?.[0]?.axisValueLabel ?? '';
              let html = `<div>${xVal}</div>`;

              params.forEach((p) => {
                const v = Number(p?.value) || 0;
                html += `
                  <div style="margin-top:4px;">
                    ${p.marker}
                    ${p.seriesName}: <strong>${v}</strong>
                  </div>
                `;
              });

              return html;
            },
          }
        : undefined,

      legend: {
        top: 0,
        left: 0,
        icon: 'rect',
        itemWidth: 12,
        itemHeight: 8,
        textStyle: { color: legendColor },
      },

      grid: {
        left: '6%',
        right: '6%',
        bottom: '6%',
        containLabel: true,
      },

      xAxis: {
        type: 'category',
        data: chartData.xAxis,
        boundaryGap: false,
        name: xAxisLabel,
        nameLocation: 'middle',
        nameGap: 26,
        nameTextStyle: { color: labelColor },

        axisLine: {
          show: true,
          lineStyle: {
            type: 'dashed',
            color: labelColor,
            width: 1,
          },
        },
        axisTick: { show: false },
        axisLabel: { color: labelColor },
        splitLine: { show: false },
      },

      yAxis: {
        type: 'value',
        axisLine: {
          show: true,
          lineStyle: {
            type: 'dashed',
            color: labelColor,
            width: 1,
          },
        },
        axisTick: { show: false },
        axisLabel: {
          color: labelColor,
          formatter: (v: number) => formatYAxisValue(Number(v) || 0, isCost),
        },
        splitLine: { show: false },
      },

      series: chartData.series.map((s) => {
        const isLine = s.variant === 'line';

        // Area with dots
        if (!isLine) {
          return {
            name: s.name,
            type: 'line',
            data: s.data,

            smooth: false,
            showSymbol: true,
            symbol: 'circle',
            symbolSize: 10,

            itemStyle: { color: s.color, borderWidth: 0 },

            lineStyle: {
              width: 0,
              color: s.color,
            },

            areaStyle: {
              opacity: 1,
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: s.color },
                  { offset: 1, color: 'rgba(255,255,255,0.05)' },
                ],
              },
            },
            emphasis: { focus: 'series' },
          };
        }

        // Trend line
        return {
          name: s.name,
          type: 'line',
          data: s.data,
          smooth: false,
          showSymbol: false,
          z: 10,
          itemStyle: { color: s.color },
          lineStyle: { width: 3, color: s.color },
          emphasis: { focus: 'series' },
        };
      }),
    };
  }, [chartData, colors, hasTooltip, isCost, xAxisLabel]);

  return (
    <WidgetWraper>
      <Box sx={{ backgroundColor: colors.widget.background.card }} {...containerProps}>
        {/* Header row */}
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          sx={{ minHeight: minTitleHeight }}
          gap={2}
        >
          <Stack direction="column" gap={2}>
            <Stack direction="row" alignItems="center" gap={1}>
              <Typography variant="widgetTitleNew" sx={{ color: colors.widget.text.title }}>
                <HighlightedText text={title} />
              </Typography>
              {hasTooltip && tooltipLabel && <InfoTooltip label={tooltipLabel} />}
            </Stack>

            {subTitle && (
              <Typography variant="bodyText" sx={{ color: colors.widget.text.title }}>
                {subTitle}
              </Typography>
            )}
          </Stack>

          {hasFilter && (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="flex-end"
              gap={1}
              {...childrenContainerProps}
            >
              {children}
            </Stack>
          )}
        </Stack>

        {/* Chart */}
        <Box height={height ?? 350} mt={2}>
          {!isLoading ? (
            <ReactECharts
              ref={chartRef}
              option={option}
              style={{ height: '100%', width: '100%' }}
            />
          ) : (
            <LoaderComponent pt={10} />
          )}
        </Box>
      </Box>
    </WidgetWraper>
  );
};

export default AreaTrendChart;
